import React, { useEffect, useState, useCallback } from 'react';
import PageLoader from '../components/PageLoader';
import { useNavigate } from 'react-router-dom';
import { 
  Clapperboard, 
  DollarSign, 
  Video,
  Users,
  Clock,
  CheckCircle,
  Zap, 
  Target,
  Trophy,
  TrendingUp,
  Inbox,
  ArrowRight,
  AlertTriangle
} from 'lucide-react';
import { authConfig } from '../config/auth';
import { usePageTitle } from '../hooks/usePageTitle';
import { useAuth } from '../contexts/AuthContext';
import { usePointsEnabled } from '../hooks/usePointsEnabled';
import Tooltip from '../components/Tooltip';
import EmployeeDashboard from '../components/EmployeeDashboard';
import SuggestedTrendsWidget from '../components/SuggestedTrendsWidget';
import WeeklyGamePlanWidget from '../components/WeeklyGamePlanWidget';
import ReviewTimeWidget from '../components/ReviewTimeWidget';
import '../styles/dashboard.css';

interface EmployeeDashboardData {
  earnings: {
    currentPoints: number;
    nextGoal: number | null;
    nextGoalTitle: string | null;
    pointsToNextGoal: number;
    progressPercentage: number;
    hasActiveRewards: boolean;
    canClaimReward: boolean;
    bestClaimableReward: { rewardId: string; title: string; points: number } | null;
  };
  activity: {
    totalSubmitted: number;
    totalApproved: number;
    approvalRate: number;
  };
  streak: {
    current: number;
    isActive: boolean;
  };
  rewards: {
    totalClaimed: number;
    totalValue: number;
  };
  moments: {
    totalActiveMoments: number;
  }
}

interface AdminDashboardData {
  roiEngine: {
    totalHoursSaved: number;
    budgetReclaimed: number;
    totalMomentsLaunched: number;
    activeMoments: number;
    totalApprovedAssets: number;
    config: {
      baseTradMins: number;
      launchMins: number;
      reviewMins: number;
      hourlyRate: number;
    };
  };
  rewardPayouts: {
    totalOwed: number;
    totalPaidThisMonth: number;
    totalCreditsEarned: number;
    pendingCount: number;
    fulfilledCount: number;
  };
  contentVolume: {
    totalApproved: number;
    currentMonthCount: number;
    previousMonthCount: number;
    growthPercentage: number;
    monthlyData: Array<{ month: string; count: number }>;
  };
  crewParticipation: {
    participationRate: number;
    activeContributors: number;
    totalCrew: number;
  };
  submissionReview: {
    pendingCount: number;
    overdueCount: number;
  };
}

export default function DashboardPage() {
  usePageTitle('Dashboard');
  const { token, user: currentUser } = useAuth();
  const [adminDashboardData, setAdminDashboardData] = useState<AdminDashboardData | null>(null);
  const [employeeDashboardData, setEmployeeDashboardData] = useState<EmployeeDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'superadmin';
  const pointsEnabled = usePointsEnabled();
  // Bumped when a trend is launched, so the Game Plan's launch count refreshes.
  const [gamePlanRefreshKey, setGamePlanRefreshKey] = useState(0);

  const [trendCount, setTrendCount] = useState(0);

  // Scroll to Suggested Trends only when there's a trend to launch; otherwise
  // (trends off, none left, or still loading) go to Moments to create one.
  const handleLaunchMoments = (suggestedTrendsEnabled: boolean) => {
    const trends = document.getElementById('suggested-trends');
    if (suggestedTrendsEnabled && trendCount > 0 && trends) trends.scrollIntoView({ behavior: 'smooth', block: 'start' });
    else navigate('/moments');
  };

  const fetchAdminDashboardData = useCallback(async () => {
    try {
      if (!token) throw new Error('Not authenticated');

      const response = await fetch(
        `${authConfig.apiUrl}/api/admin/analytics/dashboard`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch dashboard data');
      }

      const data = await response.json();
      setAdminDashboardData(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchEmployeeDashboardData = useCallback(async () => {
    try {
      if (!token) throw new Error('Not authenticated');

      const response = await fetch(
        `${authConfig.apiUrl}/api/employee/analytics/dashboard`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch dashboard data');
      }

      const data = await response.json();
      setEmployeeDashboardData(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchAdminDashboardData();
      
      // Refresh every 30 seconds for live updates
      const interval = setInterval(() => {
        fetchAdminDashboardData();
      }, 30000); // 30 seconds

      return () => clearInterval(interval);
    } else {
      fetchEmployeeDashboardData();
      
      // Refresh every 30 seconds for live updates
      const interval = setInterval(() => {
        fetchEmployeeDashboardData();
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [isAdmin, fetchAdminDashboardData, fetchEmployeeDashboardData]);

  // Employee Dashboard View
  if (!isAdmin) {
    if (loading) {
      return <PageLoader />;
    }

    if (error) {
      return (
        <div className="dashboard-error">
          <strong>Error:</strong> {error}
        </div>
      );
    }

    if (!employeeDashboardData) {
      return <div className="dashboard-loading">No data available</div>;
    }

    return (
      <EmployeeDashboard
        data={employeeDashboardData}
        userName={currentUser?.firstName || 'there'}
        pointsEnabled={pointsEnabled}
      />
    );
  }

  // Admin Dashboard View
  if (loading) {
    return <PageLoader />;
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <strong>Error:</strong> {error}
      </div>
    );
  }

  if (!adminDashboardData) {
    return <div className="dashboard-loading">No data available</div>;
  }

  const { roiEngine, rewardPayouts, contentVolume, crewParticipation, submissionReview } = adminDashboardData;

  // Guard against older API responses that predate submissionReview.
  const pendingReviewCount = submissionReview?.pendingCount ?? 0;
  const overdueReviewCount = submissionReview?.overdueCount ?? 0;

  // Generate SVG path for trend graph based on real data
  const generateTrendPath = (data: Array<{ month: string; count: number }>) => {
    if (!data || data.length === 0) {
      return {
        linePath: 'M 0,50 L 300,50',
        areaPath: 'M 0,50 L 300,50 L 300,90 L 0,90 Z',
        points: []
      };
    }
    
    const maxCount = Math.max(...data.map(d => d.count), 1);
    const width = 300;
    const graphHeight = 90; // Graph area height (leave room for labels below)
    const topPadding = 25; // Extra padding at top for value labels
    const segmentWidth = width / (data.length - 1 || 1);
    
    const points = data.map((d, i) => {
      const x = i * segmentWidth;
      const y = topPadding + (graphHeight - topPadding) - (d.count / maxCount) * (graphHeight - topPadding);
      return { x, y, count: d.count, month: d.month };
    });
    
    // Create line path
    const linePath = points.map((p, i) => 
      `${i === 0 ? 'M' : 'L'} ${p.x},${p.y}`
    ).join(' ');
    
    // Create area path (for gradient fill)
    const areaPath = `${linePath} L ${width},${graphHeight} L 0,${graphHeight} Z`;
    
    return { linePath, areaPath, points };
  };

  const trendPaths = generateTrendPath(contentVolume.monthlyData || []);

  // Calculate costs for comparison
  const traditionalCost = roiEngine.totalApprovedAssets * roiEngine.config.baseTradMins;
  const crewcastCost =
    roiEngine.totalMomentsLaunched * roiEngine.config.launchMins +
    roiEngine.totalApprovedAssets * roiEngine.config.reviewMins;
  const maxCost = Math.max(traditionalCost, crewcastCost);
  const traditionalPercent = (traditionalCost / maxCost) * 100;
  const crewcastPercent = (crewcastCost / maxCost) * 100;

  // Gauge calculation
  const circumference = 2 * Math.PI * 82; // radius = 82
  const offset = circumference - (crewParticipation.participationRate / 100) * circumference;

  const idleContributors = crewParticipation.totalCrew - crewParticipation.activeContributors;

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1 className="dashboard-greeting" style={{ textTransform: 'none', display: 'flex', alignItems: 'center', gap: '8px' }}>
          Hey, {currentUser?.firstName}! <Clapperboard size={28} color="#00bf62" />
        </h1>
        <div className="dashboard-status">
          <span className="status-indicator"></span>
          LIVE
        </div>
      </div>

      {/* Weekly Game Plan + last week's Pulse, shared across the company's admins */}
      <WeeklyGamePlanWidget
        refreshKey={gamePlanRefreshKey}
        onLaunchMoments={handleLaunchMoments}
        onReviewSubmissions={() => navigate('/review-submissions')}
      />

      {/* Pending Submissions Review Prompt — alerts admins to items awaiting Approve/Reject */}
      <div className={`review-alert ${pendingReviewCount > 0 ? 'has-pending' : 'clear'}`}>
        <div className="review-alert-icon">
          {pendingReviewCount > 0 ? <Inbox size={24} /> : <CheckCircle size={24} />}
        </div>
        <div className="review-alert-body">
          <div className="review-alert-title">
            {pendingReviewCount > 0
              ? `You have ${pendingReviewCount} submission${pendingReviewCount === 1 ? '' : 's'} awaiting review.`
              : 'Your review queue is clear.'}
          </div>
          <div className="review-alert-subtitle">
            {pendingReviewCount > 0
              ? 'Approve or reject them to keep your content pipeline moving.'
              : 'No submissions are waiting on you right now. Nicely done!'}
          </div>
          {pendingReviewCount > 0 && overdueReviewCount > 0 && (
            <div className="review-alert-overdue">
              <AlertTriangle size={16} />
              <span>
                {overdueReviewCount} submission{overdueReviewCount === 1 ? ' is' : 's are'} overdue for review (waiting 24h+)
              </span>
            </div>
          )}
        </div>
        {pendingReviewCount > 0 && (
          <button
            className="review-alert-cta"
            onClick={() => navigate('/review-submissions')}
          >
            View Submissions <ArrowRight size={16} />
          </button>
        )}
      </div>

      {/* Suggested Trends — toggled per company via settings.suggestedTrendsEnabled (default off) */}
      {(currentUser?.companySettings?.suggestedTrendsEnabled ?? false) && (
        <div id="suggested-trends" className="suggested-trends-anchor">
          <SuggestedTrendsWidget
            onLaunched={() => setGamePlanRefreshKey((k) => k + 1)}
            onCountChange={setTrendCount}
          />
        </div>
      )}

      {/* Average Review Time — company-wide, own full-width row */}
      <div className="dashboard-grid-single">
        <ReviewTimeWidget />
      </div>

      {/* Row 1: Assets Generated + Team Pulse */}
      <div className="dashboard-grid">
        {/* Content Volume Widget */}
        <div className="dashboard-widget">
          <div className="widget-header">
            <h3 className="widget-title">
              <span className="widget-title-icon">
                <Video size={20} color="#00bf62" />
              </span>
              Assets Generated
            </h3>
          </div>

          <div className="volume-header">
            <div className="volume-label">Total Approved Assets</div>
            <div className="volume-main">
              <div className="volume-value">{contentVolume.totalApproved}</div>
              {contentVolume.growthPercentage !== 0 && (
                <div className="volume-growth">
                  {contentVolume.growthPercentage > 0 ? '↗' : '↘'}
                  {contentVolume.growthPercentage > 0 ? '+' : ''}
                  {contentVolume.growthPercentage}% vs. last month
                </div>
              )}
            </div>
          </div>

          <div className="trend-graph">
            <svg className="trend-line" viewBox="0 0 300 140" preserveAspectRatio="xMidYMid meet">
              <defs>
                <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="rgba(32, 92, 240, 0.2)" />
                  <stop offset="100%" stopColor="rgba(32, 92, 240, 0)" />
                </linearGradient>
              </defs>
              {/* Real data trend line */}
              <path
                d={trendPaths.linePath}
                fill="none"
                stroke="rgba(32, 92, 240, 1)"
                strokeWidth="2"
                vectorEffect="non-scaling-stroke"
              />
              <path
                d={trendPaths.areaPath}
                fill="url(#gradient)"
              />
              {/* Data points and labels */}
              {trendPaths.points && trendPaths.points.map((point, i) => (
                <g key={i}>
                  <circle
                    cx={point.x}
                    cy={point.y}
                    r="4"
                    fill="rgba(32, 92, 240, 1)"
                    stroke="white"
                    strokeWidth="2"
                  />
                  {/* Month label */}
                  <text
                    x={point.x}
                    y="110"
                    textAnchor="middle"
                    fontSize="11"
                    fill="#6b7280"
                    fontWeight="500"
                  >
                    {point.month}
                  </text>
                  {/* Value label above point - show for all points */}
                  <text
                    x={point.x}
                    y={point.y - 10}
                    textAnchor="middle"
                    fontSize="12"
                    fill="#1f2937"
                    fontWeight="600"
                  >
                    {point.count}
                  </text>
                </g>
              ))}
            </svg>
          </div>
        </div>

        {/* Team Pulse Widget */}
        <div className="dashboard-widget">
          <div className="widget-header">
            <h3 className="widget-title">
              <span className="widget-title-icon">
                <Users size={20} color="#00bf62" />
              </span>
              Team Pulse
            </h3>
            <Tooltip content="Shows the percentage of your team who have submitted at least one moment this month. Updates in real-time as employees participate." />
          </div>

          <div className="pulse-gauge-container">
            <div className="pulse-gauge">
              <svg className="gauge-svg" width="180" height="180" viewBox="0 0 180 180">
                <circle
                  className="gauge-background"
                  cx="90"
                  cy="90"
                  r="82"
                />
                <circle
                  className="gauge-progress"
                  cx="90"
                  cy="90"
                  r="82"
                  strokeDasharray={circumference}
                  strokeDashoffset={offset}
                />
              </svg>
              <div className="gauge-text">
                <span className="gauge-percentage">{crewParticipation.participationRate}%</span>
                <span className="gauge-label">Active</span>
              </div>
            </div>

            <div className="pulse-stats">
              <div className="pulse-stat">
                <span className="pulse-stat-dot active"></span>
                {crewParticipation.activeContributors} Active
              </div>
              <div className="pulse-stat">
                <span className="pulse-stat-dot idle"></span>
                {idleContributors} Idle
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Row 2: Quick Stats + Time & Budget Reclaimed */}
      <div className="dashboard-grid">
        {/* Quick Stats Widget — consolidated reward + moment stats */}
        <div className="dashboard-widget">
          <div className="widget-header">
            <h3 className="widget-title">
              <span className="widget-title-icon">
                <Zap size={20} color="#00bf62" />
              </span>
              Quick Stats
            </h3>
          </div>

          <div className="reward-metrics">
            {/* Pending Reward Claims — only when rewards/points are enabled; links to Claimed Rewards */}
            {pointsEnabled && (
              <div
                className="reward-metric clickable"
                role="button"
                tabIndex={0}
                onClick={() => navigate('/admin/claimed-rewards')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate('/admin/claimed-rewards');
                  }
                }}
              >
                <div className="reward-icon pending">
                  <Clock size={20} />
                </div>
                <div className="reward-info">
                  <div className="reward-metric-label">Pending Reward Claims</div>
                  <div className="reward-metric-value">{rewardPayouts.pendingCount}</div>
                </div>
              </div>
            )}

            {/* Active Moments — links to Moments */}
            <div
              className="reward-metric clickable"
              role="button"
              tabIndex={0}
              onClick={() => navigate('/moments')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate('/moments');
                }
              }}
            >
              <div className="reward-icon" style={{ background: 'var(--color-purple-100)', color: 'var(--color-purple-600)' }}>
                <Target size={20} />
              </div>
              <div className="reward-info">
                <div className="reward-metric-label">Active Moments</div>
                <div className="reward-metric-value">{roiEngine.activeMoments}</div>
              </div>
            </div>

            {/* Total Outstanding Credits — only when rewards/points are enabled */}
            {pointsEnabled && (
              <div className="reward-metric">
                <div className="reward-icon" style={{ background: 'var(--color-blue-100)', color: 'var(--color-blue-600)' }}>
                  <Trophy size={20} />
                </div>
                <div className="reward-info">
                  <div className="reward-metric-label">Total Outstanding Credits</div>
                  <div className="reward-metric-value">{rewardPayouts.totalCreditsEarned.toLocaleString()}</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ROI Widget — Time & Budget Reclaimed (last) */}
        <div className="dashboard-widget">
          <div className="widget-header">
            <h3 className="widget-title">
              <span className="widget-title-icon">
                <DollarSign size={20} color="#00bf62" />
              </span>
              Time & Budget Reclaimed
            </h3>
            <Tooltip content="We compare a 2.5hr traditional production cycle (briefing, filming, editing) against your CrewCast activity (launching & approving). Financial impact is calculated based on a $75/hr production rate." />
          </div>

          <div className="roi-main-stats">
            <div className="roi-stat">
              <div className="roi-value">
                ${roiEngine.budgetReclaimed.toLocaleString()}
              </div>
              <div className="roi-label">Budget Saved</div>
            </div>
            <div className="roi-stat">
              <div className="roi-value">
                {roiEngine.totalHoursSaved.toLocaleString()}hrs
              </div>
              <div className="roi-label">Time Saved</div>
            </div>
          </div>

          <div className="comparison-bars">
            <div className="comparison-row">
              <div className="comparison-label">
                <span>Traditional Agency</span>
                <span>${Math.round((traditionalCost / 60) * roiEngine.config.hourlyRate).toLocaleString()}</span>
              </div>
              <div className="comparison-bar">
                <div
                  className="comparison-fill traditional"
                  style={{ width: `${traditionalPercent}%` }}
                ></div>
              </div>
            </div>

            <div className="comparison-row">
              <div className="comparison-label">
                <span>CrewCast</span>
                <span>${Math.round((crewcastCost / 60) * roiEngine.config.hourlyRate).toLocaleString()}</span>
              </div>
              <div className="comparison-bar">
                <div
                  className="comparison-fill crewcast"
                  style={{ width: `${crewcastPercent}%` }}
                ></div>
              </div>
            </div>
          </div>

          <div className="roi-savings-badge">
            <TrendingUp size={16} style={{ marginRight: '4px' }} /> {Math.round((1 - crewcastCost / traditionalCost) * 100)}% Cost Reduction
          </div>
        </div>
      </div>
    </div>
  );
}
